import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { CurrencyEnum } from 'common/types/CurrencyType'
import { currencyFormat } from 'common/utils/currency-format'
import { css, RuleSet } from 'styled-components'
import {
  PriceElementGroupInterface,
  PriceElementPeriodInterface,
  PricePlanPeriodType,
  UserPlansResponseInterface,
} from '../price-element-interface'

export const HIGH_DENOMINATION_LOCALES = [
  PageLocaleEnum.ALBANIA,
  PageLocaleEnum.RUSSIAN,
  PageLocaleEnum.JAPANESE,
  PageLocaleEnum.TURKISH,
  PageLocaleEnum.SLOVENIAN,
  PageLocaleEnum.SERBIAN,
  PageLocaleEnum.HUNGARIAN,
  PageLocaleEnum.CZECH,
  PageLocaleEnum.INDONESIAN,
  PageLocaleEnum.HINDI,
  PageLocaleEnum.THAI,
  PageLocaleEnum.UKRAINIAN,
]
export const isHighDenominationLocale = (locale: PageLocaleEnum) =>
  HIGH_DENOMINATION_LOCALES.includes(locale)

export const UNLIMITED_THRESHOLD = 100000
export const UNLIMITED_VARIANTS_THRESHOLD = 400

export const GROUP_TITLE = {
  contacts: 'components.price_element.group.contacts_and_crm',
  emailMarketing: 'components.price_element.group.email_marketing',
  automationAndWorkflows:
    'components.price_element.group.automation_and_workflows',
  sales: 'components.price_element.group.sales',
  subaccounts: 'components.price_element.group.subaccounts',
  booking: 'components.price_element.group.booking',
  payment: 'components.price_element.group.payment',
  blogs: 'components.price_element.group.blogs',
  link: 'components.price_element.group.link',
  eCommerce: 'components.price_element.group.e_commerce',
  courses: 'components.price_element.group.courses',
  communities: 'components.price_element.group.communities',
  affiliate: 'components.price_element.group.affiliate',
  support: 'components.price_element.group.support',
  team: 'components.price_element.group.team',
  integrations: 'components.price_element.group.integrations',
  perks: 'components.price_element.group.perks',
}

export const PRICE_PLAN_TITLE = {
  contacts: 'components.price_element.plans.contacts',
  emailsSent: 'components.price_element.plans.email_sent',
  crmPipelines: 'components.price_element.plans.crm_pipelines',
  emailCampaigns: 'components.price_element.plans.email_campaigns',
  emailNewsletters: 'components.price_element.plans.email_newsletters',
  automationRules: 'components.price_element.plans.automation_rules',
  workflows: 'components.price_element.plans.workflows',
  tags: 'components.price_element.plans.tags',
  api: 'components.price_element.plans.api',
  salesFunnels: 'components.price_element.plans.sales_funnels',
  salesFunnelsSteps: 'components.price_element.plans.sales_funnels_steps',
  deadlineFunnels: 'components.price_element.plans.deadline_funnels',
  evergreenWebinars: 'components.price_element.plans.evergreen_webinars',
  tests: 'components.price_element.plans.tests',
  customDomains: 'components.price_element.plans.custom_domains',
  fileStorageSpace: 'components.price_element.plans.file_storage_space',
  clickUpsells: 'components.price_element.plans.click_upsells',
  orderBumps: 'components.price_element.plans.order_bumps',
  transactionFees: 'components.price_element.plans.transaction_fees',
  events: 'components.price_element.plans.events',
  bookings: 'components.price_element.plans.bookings',
  stripe: 'components.price_element.plans.stripe',
  applePay: 'components.price_element.plans.apple_pay',
  paypal: 'components.price_element.plans.paypal',
  razorpay: 'components.price_element.plans.razorpay',
  flutterwave: 'components.price_element.plans.flutterwave',
  mercadoPago: 'components.price_element.plans.mercado_pago',
  paystack: 'components.price_element.plans.paystack',
  xendit: 'components.price_element.plans.xendit',
  cash: 'components.price_element.plans.cash',
  variantsPerProduct: 'components.price_element.plans.variants_per_product',
  blogs: 'components.price_element.plans.blogs',
  blogPosts: 'components.price_element.plans.blogs_posts',
  mobileLink: 'components.price_element.plans.mobile_link',
  mobileStore: 'components.price_element.plans.mobile_store',
  physicalProducts: 'components.price_element.plans.physical_products',
  shippingManagement: 'components.price_element.plans.shipping_management',
  inventoryManagement: 'components.price_element.plans.inventory_management',
  courses: 'components.price_element.plans.courses',
  students: 'components.price_element.plans.students',
  studentsActivityHistory:
    'components.price_element.plans.students_activity_history',
  communities: 'components.price_element.plans.communities',
  communityMembers: 'components.price_element.plans.community_members',
  affiliateProgram: 'components.price_element.plans.affiliate_program',
  support: 'components.price_element.plans.support',
  freeMigration: 'components.price_element.plans.free_migration',
  kickstart: 'components.price_element.plans.kickstart',
  assistantAccounts: 'components.price_element.plans.assistant_accounts',
  googleSheetsIntegration:
    'components.price_element.plans.google_sheets_integration',
  googleMeetIntegration:
    'components.price_element.plans.google_meet_integration',
  googleCalendarIntegration:
    'components.price_element.plans.google_calendar_integration',
  activeCampaignIntegration:
    'components.price_element.plans.active_campaign_integration',
  sendGridIntegration: 'components.price_element.plans.send_grid_integration',
  zoomIntegration: 'components.price_element.plans.zoom_integration',
  subaccounts: 'components.price_element.group.subaccounts',
  couponCodes: 'components.price_element.plans.coupon_codes',
  earlyAccess: 'components.price_element.plans.early_access',
}

export const getPriceGroupDefault = (
  planCount = 4,
): PriceElementGroupInterface[] => {
  const getUnknownArray = () => Array(planCount).fill('unknown')

  return [
    {
      id: 1,
      icon: 'contactsAndCrm',
      title: GROUP_TITLE.contacts,
      items: [
        {
          title: PRICE_PLAN_TITLE.contacts,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.tags,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.crmPipelines,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 15,
      icon: 'funnel',
      title: GROUP_TITLE.emailMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.emailsSent,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailCampaigns,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailNewsletters,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 16,
      icon: 'automationsAndWorkflows',
      title: GROUP_TITLE.automationAndWorkflows,
      items: [
        {
          title: PRICE_PLAN_TITLE.automationRules,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.workflows,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 2,
      icon: 'funnel',
      title: GROUP_TITLE.sales,
      items: [
        {
          title: PRICE_PLAN_TITLE.salesFunnels,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.salesFunnelsSteps,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.deadlineFunnels,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.evergreenWebinars,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.tests,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.customDomains,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.fileStorageSpace,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.clickUpsells,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.orderBumps,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 14,
      icon: 'subaccounts',
      title: GROUP_TITLE.subaccounts,
      tooltip: `components.price_element.tooltip.subaccounts`,
      items: [
        {
          title: PRICE_PLAN_TITLE.subaccounts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 3,
      icon: 'booking',
      title: GROUP_TITLE.booking,
      items: [
        {
          title: PRICE_PLAN_TITLE.events,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.bookings,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 4,
      icon: 'payment',
      title: GROUP_TITLE.payment,
      items: [
        {
          title: PRICE_PLAN_TITLE.stripe,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.applePay,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.paypal,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.razorpay,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.flutterwave,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.mercadoPago,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.paystack,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.xendit,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.cash,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 5,
      icon: 'blogs',
      title: GROUP_TITLE.blogs,
      items: [
        {
          title: PRICE_PLAN_TITLE.blogs,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.blogPosts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 6,
      icon: 'link',
      title: GROUP_TITLE.link,
      items: [
        {
          title: PRICE_PLAN_TITLE.mobileLink,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.mobileStore,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 7,
      icon: 'e-commerce',
      title: GROUP_TITLE.eCommerce,
      items: [
        {
          title: PRICE_PLAN_TITLE.physicalProducts,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.shippingManagement,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.inventoryManagement,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.cash,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.variantsPerProduct,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.couponCodes,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 8,
      icon: 'courses',
      title: GROUP_TITLE.courses,
      items: [
        {
          title: PRICE_PLAN_TITLE.courses,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.students,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.studentsActivityHistory,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 9,
      icon: 'communities',
      title: GROUP_TITLE.communities,
      items: [
        {
          title: PRICE_PLAN_TITLE.communities,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.communityMembers,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 10,
      icon: 'affiliate',
      title: GROUP_TITLE.affiliate,
      items: [
        {
          title: PRICE_PLAN_TITLE.affiliateProgram,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 11,
      icon: 'support',
      title: GROUP_TITLE.support,
      items: [
        {
          title: PRICE_PLAN_TITLE.support,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.kickstart,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 12,
      icon: 'team',
      title: GROUP_TITLE.team,
      items: [
        {
          title: PRICE_PLAN_TITLE.assistantAccounts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 13,
      icon: 'integrations',
      title: GROUP_TITLE.integrations,
      items: [
        {
          title: PRICE_PLAN_TITLE.googleSheetsIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.googleCalendarIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.googleMeetIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.zoomIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.activeCampaignIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.sendGridIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.api,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: 17,
      icon: 'perks',
      title: GROUP_TITLE.perks,
      items: [
        {
          title: PRICE_PLAN_TITLE.freeMigration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.earlyAccess,
          plans: getUnknownArray(),
        },
      ],
    },
  ]
}

export const getPriceGroup = (
  period: PricePlanPeriodType,
  plans: UserPlansResponseInterface[],
): PriceElementGroupInterface[] => {
  if (!plans.length) {
    return getPriceGroupDefault()
  }

  const planCount = plans.length

  const getPlan = (
    number: number,
    unlimitedThreshold = UNLIMITED_THRESHOLD,
  ): string => {
    return number >= unlimitedThreshold
      ? 'Unlimited'
      : new Intl.NumberFormat('en-IN').format(number)
  }

  const getUnlimitedArray = () => Array(planCount).fill('Unlimited')
  const getBooleanArray = (value: boolean) => Array(planCount).fill(value)
  const getZeroPercentArray = () => Array(planCount).fill('0%')

  const getProgressiveArray = (values: (string | boolean)[]) =>
    values.slice(0, planCount)

  const isAnnually = period === 'annually'

  return [
    {
      id: 1,
      icon: 'contactsAndCrm',
      title: GROUP_TITLE.contacts,
      items: [
        {
          title: PRICE_PLAN_TITLE.contacts,
          plans: plans.map(el => getPlan(el.maxContacts)),
        },
        {
          title: PRICE_PLAN_TITLE.tags,
          plans: plans.map(el => getPlan(el.maxTags)),
        },
        {
          title: PRICE_PLAN_TITLE.crmPipelines,
          plans: plans.map(el => getPlan(el.maxCrmPipelines)),
        },
      ],
    },
    {
      id: 15,
      icon: 'funnel',
      title: GROUP_TITLE.emailMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.emailsSent,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailCampaigns,
          plans: plans.map(el => getPlan(el.maxCampaigns)),
        },
        {
          title: PRICE_PLAN_TITLE.emailNewsletters,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 16,
      icon: 'automationsAndWorkflows',
      title: GROUP_TITLE.automationAndWorkflows,
      items: [
        {
          title: PRICE_PLAN_TITLE.automationRules,
          plans: plans.map(el => getPlan(el.maxAutomationRules)),
        },
        {
          title: PRICE_PLAN_TITLE.workflows,
          plans: plans.map(el => getPlan(el.maxWorkflows)),
        },
      ],
    },
    {
      id: 2,
      icon: 'funnel',
      title: GROUP_TITLE.sales,
      items: [
        {
          title: PRICE_PLAN_TITLE.salesFunnels,
          plans: plans.map(el => getPlan(el.maxFunnels)),
        },
        {
          title: PRICE_PLAN_TITLE.salesFunnelsSteps,
          plans: plans.map(el => getPlan(el.maxFunnelSteps)),
        },
        {
          title: PRICE_PLAN_TITLE.deadlineFunnels,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.evergreenWebinars,
          plans: plans.map(el => getPlan(el.maxWebinars)),
        },
        {
          title: PRICE_PLAN_TITLE.tests,
          plans: plans.map(el => getPlan(el.maxSplitTests)),
        },
        {
          title: PRICE_PLAN_TITLE.customDomains,
          plans: plans.map(el => getPlan(el.maxUserDomains)),
        },
        {
          title: PRICE_PLAN_TITLE.fileStorageSpace,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.clickUpsells,
          plans: plans.map(el => getPlan(el.maxUpsells)),
        },
        {
          title: PRICE_PLAN_TITLE.orderBumps,
          plans: plans.map(el => getPlan(el.maxOrderBumps)),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getZeroPercentArray(),
        },
      ],
    },
    {
      id: 14,
      icon: 'subaccounts',
      title: GROUP_TITLE.subaccounts,
      tooltip: `components.price_element.tooltip.subaccounts`,
      items: [
        {
          title: PRICE_PLAN_TITLE.subaccounts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 3,
      icon: 'booking',
      title: GROUP_TITLE.booking,
      items: [
        {
          title: PRICE_PLAN_TITLE.events,
          plans: plans.map(el => {
            return getPlan(el.maxBookingEvents)
          }),
        },
        {
          title: PRICE_PLAN_TITLE.bookings,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 4,
      icon: 'payment',
      title: GROUP_TITLE.payment,
      items: [
        { title: PRICE_PLAN_TITLE.stripe, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.applePay, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.paypal, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.razorpay, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.flutterwave,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.mercadoPago,
          plans: getBooleanArray(true),
        },
        { title: PRICE_PLAN_TITLE.paystack, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.xendit, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.cash, plans: getBooleanArray(true) },
      ],
    },
    {
      id: 5,
      icon: 'blogs',
      title: GROUP_TITLE.blogs,
      items: [
        {
          title: PRICE_PLAN_TITLE.blogs,
          plans: plans.map(el => getPlan(el.maxBlogs)),
        },
        {
          title: PRICE_PLAN_TITLE.blogPosts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 6,
      icon: 'link',
      title: GROUP_TITLE.link,
      items: [
        { title: PRICE_PLAN_TITLE.mobileLink, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.mobileStore,
          plans: getProgressiveArray(['1', '3', '10', 'Unlimited']),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getZeroPercentArray(),
        },
      ],
    },
    {
      id: 7,
      icon: 'e-commerce',
      title: GROUP_TITLE.eCommerce,
      items: [
        {
          title: PRICE_PLAN_TITLE.physicalProducts,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.shippingManagement,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.inventoryManagement,
          plans: getBooleanArray(true),
        },
        { title: PRICE_PLAN_TITLE.cash, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.variantsPerProduct,
          plans: plans.map(el =>
            getPlan(el.maxProductVariants, UNLIMITED_VARIANTS_THRESHOLD),
          ),
        },
        {
          title: PRICE_PLAN_TITLE.couponCodes,
          plans: plans.map(el => getPlan(el.maxCoupons)),
        },
      ],
    },
    {
      id: 8,
      icon: 'courses',
      title: GROUP_TITLE.courses,
      items: [
        {
          title: PRICE_PLAN_TITLE.courses,
          plans: plans.map(el => getPlan(el.maxMembershipCourses)),
        },
        {
          title: PRICE_PLAN_TITLE.students,
          plans: getProgressiveArray([
            '500',
            'Unlimited',
            'Unlimited',
            'Unlimited',
          ]),
        },
        {
          title: PRICE_PLAN_TITLE.studentsActivityHistory,
          plans: getBooleanArray(true),
        },
      ],
    },
    {
      id: 9,
      icon: 'communities',
      title: GROUP_TITLE.communities,
      items: [
        {
          title: PRICE_PLAN_TITLE.communities,
          plans: plans.map(el => getPlan(el.maxCommunities)),
        },
        {
          title: PRICE_PLAN_TITLE.communityMembers,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 10,
      icon: 'affiliate',
      title: GROUP_TITLE.affiliate,
      items: [
        {
          title: PRICE_PLAN_TITLE.affiliateProgram,
          plans: getBooleanArray(true),
        },
      ],
    },
    {
      id: 11,
      icon: 'support',
      title: GROUP_TITLE.support,
      items: [
        { title: PRICE_PLAN_TITLE.support, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.kickstart,
          plans: getProgressiveArray([false, isAnnually, isAnnually, true]),
        },
      ],
    },
    {
      id: 12,
      icon: 'team',
      title: GROUP_TITLE.team,
      items: [
        {
          title: PRICE_PLAN_TITLE.assistantAccounts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: 13,
      icon: 'integrations',
      title: GROUP_TITLE.integrations,
      items: [
        {
          title: PRICE_PLAN_TITLE.googleSheetsIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.googleCalendarIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.googleMeetIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.zoomIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.activeCampaignIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.sendGridIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.api,
          plans: getProgressiveArray(['30', '60', '120', 'Unlimited']),
        },
      ],
    },
    {
      id: 17,
      icon: 'perks',
      title: GROUP_TITLE.perks,
      items: [
        {
          title: PRICE_PLAN_TITLE.freeMigration,
          plans: getProgressiveArray([false, isAnnually, isAnnually, true]),
        },
        {
          title: PRICE_PLAN_TITLE.earlyAccess,
          plans: getProgressiveArray([false, false, false, true]),
        },
      ],
    },
  ]
}

export const getDefaultPricePeriod = (
  count: number,
  period: PricePlanPeriodType = 'monthly',
): PriceElementPeriodInterface[] =>
  [...Array(count).keys()].map(el => ({
    id: el,
    title: null,
    cost: null,
    currency: null,
    isFree: false,
    period:
      period === 'monthly'
        ? 'components.price_element.period.month'
        : 'components.price_element.period.year',
  }))

export const getPricePeriod = (
  response: UserPlansResponseInterface[],
  btnType: PricePlanPeriodType = 'monthly',
  locale: PageLocaleEnum = PageLocaleEnum.ENGLISH,
): PriceElementPeriodInterface[] =>
  response.map(el => {
    const cost = btnType === 'monthly' ? el.monthCost : el.annualCost
    const period =
      btnType === 'monthly'
        ? 'components.price_element.period.month'
        : 'components.price_element.period.year'
    return {
      id: el.id,
      title: el.title,
      currency: el.currency,
      cost: currencyFormat(
        cost / 100,
        el.currency || CurrencyEnum.EURO,
        locale,
        true,
        'narrowSymbol',
      ),
      isFree: cost === 0,
      period,
    }
  })

export const getScrollSnapStyles = (): RuleSet => css`
  width: 50%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
`

export const getPricePlanDescription = (id: number) => {
  switch (id) {
    case 40:
      return 'components.price_element.freemium_description'
    case 53:
      return 'components.price_element.startup_description'
    case 43:
      return 'components.price_element.webinar_description'
    case 44:
      return 'components.price_element.unlimited_description'
    default:
      return ''
  }
}
